import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchProducts } from '../lib/cms';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    async function loadData() {
      const data = await fetchProducts();
      setProducts(data.filter(p => p.active !== false));
      setLoading(false);
    }
    loadData();
  }, []);

  // Unique filter lists from terms
  const categories = [
    { slug: 'all', label: 'Alle' },
    { slug: 'tables', label: 'Borde' },
    { slug: 'sofas-seatings', label: 'Sofaer & Stole' },
    { slug: 'other-furnitures', label: 'Andre møbler' },
    { slug: 'uncategorized', label: 'Uklassificeret' }
  ];

  const materials = [
    { slug: 'all', label: 'Alle materialer' },
    { slug: 'stainless-steel', label: 'Rustfrit stål' },
    { slug: 'oak', label: 'Egetræ' },
    { slug: 'slate', label: 'Skifer' },
    { slug: 'granite', label: 'Granit' },
    { slug: 'marble', label: 'Marmor' },
    { slug: 'leather', label: 'Læder' },
    { slug: 'wool', label: 'Uld' },
    { slug: 'glass', label: 'Glas' }
  ];

  const types = [
    { slug: 'all', label: 'Alle typer' },
    { slug: 'coffee-table', label: 'Sofabord' },
    { slug: 'dining-table', label: 'Spisebord' },
    { slug: 'console-table', label: 'Konsolbord' },
    { slug: 'side-table', label: 'Sidebord' },
    { slug: 'daybed', label: 'Puff / Daybed' },
    { slug: 'chair', label: 'Stol' },
    { slug: 'mirror', label: 'Spejl' },
    { slug: 'wine-rack', label: 'Vinreol' },
    { slug: 'coat-hanger', label: 'Knage' }
  ];

  // Filter logic
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.short_description && product.short_description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
      product.terms?.['product-category']?.includes(selectedCategory);
      
    const matchesMaterial = selectedMaterial === 'all' || 
      product.terms?.['product-material']?.includes(selectedMaterial);
      
    const matchesType = selectedType === 'all' || 
      product.terms?.['product-type']?.includes(selectedType);

    return matchesSearch && matchesCategory && matchesMaterial && matchesType;
  });

  return (
    <div className="py-12 bg-black animate-fade-in">
      <Helmet>
        <title>Møbler & Design – Heine Design</title>
        <meta name="description" content="Udforsk vores fulde kollektion af eksklusive, håndlavede danske designmøbler. Filtrer efter kategori, materiale eller produkttype." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="space-y-4 mb-12">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
            Kollektion
          </span>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white uppercase">
            Danish Design Furniture
          </h1>
          <div className="h-[1px] w-20 bg-brand-oak" />
        </div>

        {/* Search and Filters Bar */}
        <div className="bg-brand-light border border-brand-gray rounded-sm p-6 mb-12 space-y-6">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Søg efter produkter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-brand-gray rounded-sm px-12 py-3 text-sm text-white placeholder-brand-charcoal/40 focus:outline-none focus:border-brand-oak transition-colors duration-300"
            />
            <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-charcoal/40 hover:text-brand-oak transition-colors"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            )}
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Category Filter */}
            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                Kategori
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-black border border-brand-gray rounded-sm px-4 py-2.5 text-xs text-brand-charcoal focus:outline-none focus:border-brand-oak transition-colors duration-300"
              >
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Material Filter */}
            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                Materiale
              </label>
              <select
                value={selectedMaterial}
                onChange={(e) => setSelectedMaterial(e.target.value)}
                className="w-full bg-black border border-brand-gray rounded-sm px-4 py-2.5 text-xs text-brand-charcoal focus:outline-none focus:border-brand-oak transition-colors duration-300"
              >
                {materials.map((mat) => (
                  <option key={mat.slug} value={mat.slug}>
                    {mat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                Produkttype
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full bg-black border border-brand-gray rounded-sm px-4 py-2.5 text-xs text-brand-charcoal focus:outline-none focus:border-brand-oak transition-colors duration-300"
              >
                {types.map((type) => (
                  <option key={type.slug} value={type.slug}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filter Badges / Reset */}
          {(selectedCategory !== 'all' || selectedMaterial !== 'all' || selectedType !== 'all' || searchQuery) && (
            <div className="flex items-center justify-between border-t border-brand-gray pt-4 text-xs">
              <span className="text-brand-charcoal">
                Fundet: {filteredProducts.length} {filteredProducts.length === 1 ? 'produkt' : 'produkter'}
              </span>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedMaterial('all');
                  setSelectedType('all');
                  setSearchQuery('');
                }}
                className="text-white hover:text-brand-oak transition-colors duration-300 font-medium uppercase tracking-wider"
              >
                Nulstil alle filtre
              </button>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-oak" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-brand-gray rounded-sm">
            <i className="fa-regular fa-folder-open text-4xl text-brand-charcoal/30 mb-4 block" />
            <h3 className="text-base font-medium text-white uppercase tracking-wider mb-2">
              Ingen produkter fundet
            </h3>
            <p className="text-xs text-brand-charcoal">
              Prøv at ændre dine søgeord eller filterindstillinger.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/produkter/${product.slug}`}
                className="group flex flex-col bg-brand-light border border-brand-gray rounded-sm overflow-hidden transition-all duration-300 hover:shadow-black/40 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="aspect-square overflow-hidden bg-black relative rounded-sm">
                  <img
                    src={product.product_images?.[0] || product.gallery?.[0]}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 rounded-sm"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-xs tracking-wider uppercase font-medium text-white group-hover:text-brand-oak transition-colors duration-300 line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {product.terms?.['product-material']?.slice(0, 2).map((mat) => (
                        <span key={mat} className="text-[9px] bg-black text-brand-charcoal px-2 py-0.5 rounded-sm uppercase tracking-wider">
                          {mat.replace('-', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {product.price}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
